import { RuleSet, fact, all, Rule } from '../core/rules/rule-engine.js'; 

/**
 * The facts the member-directory capability rules evaluate against. This is the
 * single place that maps raw state (admin flag, group manageability) onto named
 * facts, so feature code never inspects those raw values directly.
 */
export interface DirectoryRuleContext {
    /** The current logged-in user is an EUM admin. */
    isAdmin: boolean;
    /** The active group can be managed by the current user (has manage rights). */
    groupManageable: boolean;
}

const isAdmin: Rule<DirectoryRuleContext> = fact('isAdmin');
const groupManageable: Rule<DirectoryRuleContext> = fact('groupManageable');

/**
 * Declarative capability table for the member directory. Render/behaviour code
 * consults the resulting booleans (`caps.canEditUser`, `caps.canRemoveFromGroup`)
 * rather than re-deriving the rules inline. New admin capabilities can be added
 * here without touching component logic.
 */
export const directoryRules = new RuleSet({
    /** Show the Edit User button and open the edit modal. */
    canEditUser: all(isAdmin, groupManageable),
    /** Show the Remove-from-group action within the edit modal. */
    canRemoveFromGroup: all(isAdmin, groupManageable),
    /** Show the "Add user(s)" toolbar action and its flows. */
    canAddUsers: all(isAdmin, groupManageable),
    /** Show the "Email users" toolbar action. */
    canEmailUsers: all(isAdmin),
    /** Show the "Copy users" toolbar action. */
    canCopyUsers: all(isAdmin),
    /** Show the "Export users" toolbar action. */
    canExportUsers: all(isAdmin),
});

export type DirectoryCapabilities = ReturnType<typeof directoryRules.capabilities<DirectoryRuleContext>>;
